import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { DataSourceMode, BatchFolder } from "../../../pages/playground/types";
import CollapsibleFormElement from "../../common/collapsible-form-element";

type DataSourceModeProps = {
  mode: DataSourceMode;
  onChange: (mode: DataSourceMode) => void;
  disabled?: boolean;
  readonly?: boolean;
  folders: BatchFolder[];
  selectedFolderId: string | undefined;
  onFolderChange: (folderId: string) => void;
  folderError?: string;
  // For batch creation when brand has no folder
  showBrandFolderPicker?: boolean;
  onBrandFolderPickerClick?: () => void;
};

export default function DataSourceModeSelector({
  mode,
  onChange,
  disabled = false,
  readonly = false,
  folders,
  selectedFolderId,
  onFolderChange,
  folderError,
  showBrandFolderPicker = false,
  onBrandFolderPickerClick,
}: DataSourceModeProps) {
  const handleTabChange = (
    _event: React.SyntheticEvent,
    newValue: DataSourceMode
  ) => {
    onChange(newValue);
  };

  const handleFolderChange = (event: SelectChangeEvent) => {
    onFolderChange(event.target.value);
  };

  const isDisabled = disabled || readonly;

  return (
    <CollapsibleFormElement title="Data Source" hasError={!!folderError}>
      {readonly && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mb: 1, display: "block" }}
        >
          Data source cannot be changed after batch creation
        </Typography>
      )}

      <Tabs
        value={mode}
        onChange={handleTabChange}
        aria-label="data source mode"
        sx={{ mb: 2 }}
      >
        <Tab label="Manual Upload" value="manual" disabled={isDisabled} />
        <Tab label="Google Drive" value="gdrive" disabled={isDisabled} />
      </Tabs>

      {mode === "gdrive" ? (
        <Box>
          {showBrandFolderPicker ? (
            <>
              <Button
                variant="outlined"
                onClick={onBrandFolderPickerClick}
                fullWidth
              >
                Select brand GDrive folder first
              </Button>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 1, display: "block" }}
              >
                The brand needs a Google Drive folder before you can select a
                batch folder
              </Typography>
            </>
          ) : (
            <>
              <FormControl
                fullWidth
                size="small"
                error={!!folderError}
                disabled={readonly}
              >
                <InputLabel>GDrive Folder</InputLabel>
                <Select
                  value={selectedFolderId || ""}
                  onChange={handleFolderChange}
                  label="GDrive Folder"
                >
                  {folders.map((folder) => (
                    <MenuItem key={folder.id} value={folder.id}>
                      {folder.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {folderError && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ mt: 0.5, display: "block" }}
                >
                  {folderError}
                </Typography>
              )}
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 1, display: "block" }}
              >
                Master file and images will be loaded from Google Drive
              </Typography>
            </>
          )}
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary">
          Master file and images will be uploaded manually
        </Typography>
      )}
    </CollapsibleFormElement>
  );
}
